import { useState } from 'react'
import { Zap, TrendingUp, Users, Target, Lightbulb, BarChart3, CheckCircle, AlertCircle } from 'lucide-react'

export default function AIMarketingAssistantPage() {
  const [campaigns, setCampaigns] = useState([
    { id: 1, name: 'Summer Sale Campaign', status: 'Active', spend: 1500, impressions: 45000, clicks: 2250, ctr: 5.0, roi: 320 },
    { id: 2, name: 'Brand Awareness', status: 'Active', spend: 2000, impressions: 78000, clicks: 3510, ctr: 4.5, roi: 280 },
    { id: 3, name: 'Product Launch', status: 'Paused', spend: 800, impressions: 12000, clicks: 480, ctr: 4.0, roi: 215 },
  ])

  const [isGenerating, setIsGenerating] = useState(false)
  const [suggestions, setSuggestions] = useState(null)

  const totalSpend = campaigns.reduce((sum, c) => sum + c.spend, 0)
  const totalImpressions = campaigns.reduce((sum, c) => sum + c.impressions, 0)
  const totalClicks = campaigns.reduce((sum, c) => sum + c.clicks, 0)
  const avgROI = Math.round(campaigns.reduce((sum, c) => sum + c.roi, 0) / campaigns.length)

  const generateAdSuggestions = () => {
    setIsGenerating(true)
    setTimeout(() => {
      setSuggestions({
        adIdeas: [
          { id: 1, headline: 'Limited Time: 40% Off Everything', copy: 'Don\'t miss out on our biggest sale of the year. Shop now and save big!', targetAudience: 'Previous Customers (18-45)', estimatedROI: 425 },
          { id: 2, headline: 'Exclusive: New Product Launch Today', copy: 'Be the first to try our revolutionary new product. Limited stock available.', targetAudience: 'Tech Enthusiasts (25-55)', estimatedROI: 380 },
          { id: 3, headline: 'Free Shipping on Orders Over $50', copy: 'Get free shipping on your next purchase. Use code FREESHIP at checkout.', targetAudience: 'Cart Abandoners', estimatedROI: 360 },
        ],
        budgetRecommendations: [
          { platform: 'Facebook Ads', recommendedBudget: 800, currentBudget: 600, reason: 'High CTR and lowest CPC', potentialGain: 'Additional 120 clicks/week' },
          { platform: 'Google Ads', recommendedBudget: 900, currentBudget: 700, reason: 'Strong conversion intent', potentialGain: 'Additional 95 conversions/month' },
          { platform: 'Instagram Ads', recommendedBudget: 400, currentBudget: 500, reason: 'Lower engagement than expected', potentialGain: 'Reallocate to better performers' },
        ],
        audienceInsights: [
          { segment: 'High-Value Customers', size: '5,200 people', engagement: 8.5, recommendedStrategy: 'Premium product targeting' },
          { segment: 'New Visitors', size: '12,800 people', engagement: 4.2, recommendedStrategy: 'Brand awareness content' },
          { segment: 'Cart Abandoners', size: '3,400 people', engagement: 6.8, recommendedStrategy: 'Retargeting with discounts' },
        ]
      })
      setIsGenerating(false)
    }, 2500)
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-white mb-2">AI Marketing Assistant</h1>
        <p className="text-blue-200">Create, optimize, and manage your marketing campaigns with AI recommendations</p>
      </div>

      {/* Campaign Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-blue-600/20 to-cyan-600/20 rounded-lg border border-blue-500/30 p-6">
          <p className="text-blue-300/70 text-sm mb-1">Total Spend</p>
          <p className="text-3xl font-bold text-blue-400">${totalSpend.toLocaleString()}</p>
          <p className="text-xs text-blue-300 mt-2">Across all campaigns</p>
        </div>

        <div className="bg-gradient-to-br from-purple-600/20 to-pink-600/20 rounded-lg border border-purple-500/30 p-6">
          <p className="text-purple-300/70 text-sm mb-1">Total Impressions</p>
          <p className="text-3xl font-bold text-purple-400">{(totalImpressions / 1000).toFixed(0)}K</p>
          <p className="text-xs text-purple-300 mt-2">People reached</p>
        </div>

        <div className="bg-gradient-to-br from-green-600/20 to-emerald-600/20 rounded-lg border border-green-500/30 p-6">
          <p className="text-green-300/70 text-sm mb-1">Total Clicks</p>
          <p className="text-3xl font-bold text-green-400">{totalClicks.toLocaleString()}</p>
          <p className="text-xs text-green-300 mt-2">User interactions</p>
        </div>

        <div className="bg-gradient-to-br from-orange-600/20 to-red-600/20 rounded-lg border border-orange-500/30 p-6">
          <p className="text-orange-300/70 text-sm mb-1">Average ROI</p>
          <p className="text-3xl font-bold text-orange-400">{avgROI}%</p>
          <p className="text-xs text-orange-300 mt-2">Return on investment</p>
        </div>
      </div>

      {/* AI Suggestions Section */}
      <div className="bg-gradient-to-br from-purple-600/20 to-blue-600/20 rounded-lg border border-purple-500/30 p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="text-2xl font-bold text-white">AI-Powered Ad Suggestions</h2>
            <p className="text-blue-200 mt-2">Get AI-generated ad copy, targeting recommendations, and budget optimization</p>
          </div>
          <Lightbulb size={32} className="text-purple-400" />
        </div>

        <button
          onClick={generateAdSuggestions}
          disabled={isGenerating}
          className="w-full px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg font-bold hover:shadow-lg hover:shadow-purple-500/50 transition-all disabled:opacity-50 flex items-center justify-center space-x-2 mb-6"
        >
          <Zap size={20} />
          <span>{isGenerating ? 'Generating Suggestions...' : 'Generate Ad Suggestions with AI'}</span>
        </button>

        {suggestions && (
          <div className="space-y-6">
            {/* Ad Ideas */}
            <div>
              <h3 className="text-lg font-bold text-white mb-4">Recommended Ad Headlines & Copy</h3>
              <div className="space-y-3">
                {suggestions.adIdeas.map((ad) => (
                  <div key={ad.id} className="bg-slate-800/50 rounded-lg p-4 border border-purple-500/20">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <p className="font-bold text-white text-lg">{ad.headline}</p>
                        <p className="text-blue-200 text-sm mt-1">{ad.copy}</p>
                      </div>
                      <div className="text-right ml-4">
                        <p className="text-2xl font-bold text-purple-400">{ad.estimatedROI}%</p>
                        <p className="text-xs text-blue-300">Est. ROI</p>
                      </div>
                    </div>
                    <div className="pt-2 border-t border-purple-500/20">
                      <span className="inline-block bg-purple-500/20 text-purple-300 text-xs font-semibold px-3 py-1 rounded-full">{ad.targetAudience}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Budget Recommendations */}
            <div>
              <h3 className="text-lg font-bold text-white mb-4">Budget Optimization Recommendations</h3>
              <div className="space-y-3">
                {suggestions.budgetRecommendations.map((rec, idx) => (
                  <div key={idx} className="bg-slate-800/50 rounded-lg p-4 border border-purple-500/20">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <p className="font-semibold text-white">{rec.platform}</p>
                        <p className="text-sm text-blue-200 mt-1">{rec.reason}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-blue-300 mb-1">Current → Recommended</p>
                        <p className="text-white font-bold">${rec.currentBudget} → ${rec.recommendedBudget}</p>
                      </div>
                    </div>
                    <div className="bg-slate-700/30 rounded p-2">
                      <p className="text-xs text-green-400 font-semibold">💡 {rec.potentialGain}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Audience Insights */}
            <div>
              <h3 className="text-lg font-bold text-white mb-4">Target Audience Insights</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {suggestions.audienceInsights.map((insight, idx) => (
                  <div key={idx} className="bg-slate-800/50 rounded-lg p-4 border border-purple-500/20">
                    <p className="font-semibold text-white">{insight.segment}</p>
                    <p className="text-sm text-blue-300 mt-1">{insight.size}</p>
                    <div className="flex items-center justify-between mt-3 pt-3 border-t border-purple-500/20">
                      <span className="text-xs text-blue-300">Engagement:</span>
                      <span className="text-sm font-bold text-green-400">{insight.engagement}/10</span>
                    </div>
                    <p className="text-xs text-blue-200 mt-2 italic">{insight.recommendedStrategy}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Active Campaigns */}
      <div className="bg-slate-800/50 rounded-lg border border-blue-500/30 p-6">
        <h2 className="text-xl font-bold text-white mb-4 flex items-center space-x-2">
          <BarChart3 size={24} />
          <span>Active Campaigns</span>
        </h2>

        <div className="space-y-3">
          {campaigns.map((campaign) => (
            <div key={campaign.id} className="bg-slate-700/30 rounded-lg p-4 border border-blue-500/20">
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <p className="font-bold text-white text-lg">{campaign.name}</p>
                  <div className="flex items-center space-x-3 mt-2 text-sm text-blue-300">
                    <span>Spend: ${campaign.spend}</span>
                    <span>•</span>
                    <span>Impressions: {campaign.impressions.toLocaleString()}</span>
                    <span>•</span>
                    <span>Clicks: {campaign.clicks}</span>
                  </div>
                </div>
                <div className="text-right ml-4">
                  <span className={`text-xs font-bold px-3 py-1 rounded-full ${
                    campaign.status === 'Active'
                      ? 'bg-green-500/30 text-green-300'
                      : 'bg-yellow-500/30 text-yellow-300'
                  }`}>
                    {campaign.status}
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4 pt-3 border-t border-blue-500/20">
                <div>
                  <p className="text-blue-300/70 text-xs mb-1">CTR</p>
                  <p className="text-white font-bold">{campaign.ctr.toFixed(1)}%</p>
                </div>
                <div>
                  <p className="text-blue-300/70 text-xs mb-1">ROI</p>
                  <p className="text-green-400 font-bold">{campaign.roi}%</p>
                </div>
                <div>
                  <p className="text-blue-300/70 text-xs mb-1">CPC</p>
                  <p className="text-white font-bold">${(campaign.spend / campaign.clicks).toFixed(2)}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Marketing Best Practices */}
      <div className="bg-gradient-to-br from-blue-600/20 to-cyan-600/20 rounded-lg border border-blue-500/30 p-6">
        <h2 className="text-xl font-bold text-white mb-4 flex items-center space-x-2">
          <Target size={24} />
          <span>Marketing Best Practices</span>
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-slate-800/50 rounded-lg p-4 border border-blue-500/20">
            <div className="flex items-start space-x-3">
              <CheckCircle size={20} className="text-green-400 flex-shrink-0 mt-1" />
              <div>
                <p className="font-semibold text-white">A/B Test Regularly</p>
                <p className="text-sm text-blue-200 mt-1">Test different ad copies, headlines, and images to find what works best</p>
              </div>
            </div>
          </div>

          <div className="bg-slate-800/50 rounded-lg p-4 border border-blue-500/20">
            <div className="flex items-start space-x-3">
              <CheckCircle size={20} className="text-green-400 flex-shrink-0 mt-1" />
              <div>
                <p className="font-semibold text-white">Monitor Metrics Weekly</p>
                <p className="text-sm text-blue-200 mt-1">Review CTR, CPC, and ROI weekly to identify trends early</p>
              </div>
            </div>
          </div>

          <div className="bg-slate-800/50 rounded-lg p-4 border border-blue-500/20">
            <div className="flex items-start space-x-3">
              <CheckCircle size={20} className="text-green-400 flex-shrink-0 mt-1" />
              <div>
                <p className="font-semibold text-white">Target Right Audience</p>
                <p className="text-sm text-blue-200 mt-1">Use demographic, interest, and behavior targeting for precision</p>
              </div>
            </div>
          </div>

          <div className="bg-slate-800/50 rounded-lg p-4 border border-blue-500/20">
            <div className="flex items-start space-x-3">
              <CheckCircle size={20} className="text-green-400 flex-shrink-0 mt-1" />
              <div>
                <p className="font-semibold text-white">Optimize Landing Pages</p>
                <p className="text-sm text-blue-200 mt-1">Ensure landing pages load fast and have clear calls-to-action</p>
              </div>
            </div>
          </div>

          <div className="bg-slate-800/50 rounded-lg p-4 border border-blue-500/20">
            <div className="flex items-start space-x-3">
              <CheckCircle size={20} className="text-green-400 flex-shrink-0 mt-1" />
              <div>
                <p className="font-semibold text-white">Use Retargeting</p>
                <p className="text-sm text-blue-200 mt-1">Re-engage users who visited your site but didn't convert</p>
              </div>
            </div>
          </div>

          <div className="bg-slate-800/50 rounded-lg p-4 border border-blue-500/20">
            <div className="flex items-start space-x-3">
              <CheckCircle size={20} className="text-green-400 flex-shrink-0 mt-1" />
              <div>
                <p className="font-semibold text-white">Scale Winning Campaigns</p>
                <p className="text-sm text-blue-200 mt-1">Increase budgets for campaigns with high ROI and positive ROI</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Disclaimer */}
      <div className="bg-yellow-500/10 rounded-lg border border-yellow-500/30 p-6">
        <h3 className="text-lg font-bold text-yellow-300 mb-2 flex items-center space-x-2">
          <AlertCircle size={20} />
          <span>Important Notice</span>
        </h3>
        <p className="text-yellow-200 text-sm">
          These AI suggestions are based on campaign data and marketing best practices. Always validate recommendations with your marketing team and test thoroughly before scaling budgets. Results may vary based on your specific products, audience, and market conditions.
        </p>
      </div>
    </div>
  )
}
